import {
  CATEGORIES,
  type Category,
  GALLERY_SORTS,
  GALLERY_STATES,
  type GallerySort,
  type GalleryState,
} from "@volgo/core/contract";

/**
 * Every search condition, in one value. The search bar, the address bar and the API request are all
 * derived from this, so a condition has exactly one home and cannot be shown or edited twice.
 * Language is not here on purpose: it is a tag (`language:korean`).
 */
export type SearchQuery = {
  readonly gid: number | null;
  /** title substring */
  readonly text: string;
  /** galleries must have all of these */
  readonly tags: readonly string[];
  /** galleries must have none of these */
  readonly xtags: readonly string[];
  /** any of these; empty means no results */
  readonly categories: readonly Category[];
  /** any of these; empty means the server default (listed and expunged) */
  readonly states: readonly GalleryState[];
  readonly ratingMin: number | null;
  /** null follows the user's own "hide blocked cards" setting */
  readonly blocked: "show" | "hide" | null;
  readonly sort: GallerySort;
};

export const DEFAULT_SORT: GallerySort = "gid_desc";

export const EMPTY_QUERY: SearchQuery = {
  gid: null,
  text: "",
  tags: [],
  xtags: [],
  categories: CATEGORIES,
  states: [],
  ratingMin: null,
  blocked: null,
  sort: DEFAULT_SORT,
};

/** Namespaces E-Hentai uses, in the order the gallery window lists them. */
export const NAMESPACES = [
  "artist",
  "group",
  "parody",
  "character",
  "female",
  "male",
  "mixed",
  "language",
  "cosplayer",
  "location",
  "other",
  "reclass",
  "temp",
] as const;

export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Same accepted wire form as the API's GidParam. */
export function parseGid(raw: string): number | null {
  return /^[1-9][0-9]{0,14}$/.test(raw) ? Number(raw) : null;
}

/** Splits `namespace:name`; a bare name has an empty namespace. */
export function splitTag(tag: string): { namespace: string; name: string } {
  const at = tag.indexOf(":");
  return at < 0 ? { namespace: "", name: tag } : { namespace: tag.slice(0, at), name: tag.slice(at + 1) };
}

const without = (list: readonly string[], tag: string) => list.filter((t) => t !== tag);

/** A tag is either required or excluded, never both: adding it to one side takes it off the other. */
export function addTag(q: SearchQuery, raw: string, mode: "include" | "exclude"): SearchQuery {
  const tag = normalizeTag(raw);
  if (!tag) return q;
  const tags = without(q.tags, tag);
  const xtags = without(q.xtags, tag);
  return mode === "include" ? { ...q, tags: [...tags, tag], xtags } : { ...q, tags, xtags: [...xtags, tag] };
}

export function removeTag(q: SearchQuery, raw: string): SearchQuery {
  const tag = normalizeTag(raw);
  return { ...q, tags: without(q.tags, tag), xtags: without(q.xtags, tag) };
}

export function toggleCategory(q: SearchQuery, category: Category): SearchQuery {
  const on = q.categories.includes(category);
  // Kept in contract order so the same selection always gives the same address.
  const next = CATEGORIES.filter((c) => (c === category ? !on : q.categories.includes(c)));
  return { ...q, categories: next };
}

/**
 * What the typed text means when the user presses Enter without picking a suggestion.
 * `-something` excludes a tag, `namespace:name` with a known namespace requires a tag,
 * anything else searches titles.
 */
export function applyTyped(q: SearchQuery, typed: string): SearchQuery {
  const text = typed.trim();
  if (!text) return q;
  if (text.startsWith("-") && text.length > 1) return addTag(q, text.slice(1), "exclude");
  if (text.startsWith("+") && text.length > 1) return addTag(q, text.slice(1), "include");
  const { namespace } = splitTag(normalizeTag(text));
  if ((NAMESPACES as readonly string[]).includes(namespace)) return addTag(q, text, "include");
  return { ...q, text };
}

/** How many conditions come from the conditions menu; shown as the number on its button. */
export function menuConditionCount(q: SearchQuery): number {
  return (q.ratingMin === null ? 0 : 1) + (q.states.length ? 1 : 0) + (q.blocked === null ? 0 : 1);
}

/** The filter part of a `catalog.list` or `catalog.count` request. Defaults are left out. */
export function toFilter(q: SearchQuery) {
  return {
    ...(q.gid === null ? {} : { gid: q.gid }),
    ...(q.text ? { q: q.text } : {}),
    ...(q.tags.length ? { tag: [...q.tags] } : {}),
    ...(q.xtags.length ? { xtag: [...q.xtags] } : {}),
    ...(q.categories.length < CATEGORIES.length ? { category: [...q.categories] } : {}),
    ...(q.states.length ? { state: [...q.states] } : {}),
    ...(q.ratingMin === null ? {} : { ratingMin: q.ratingMin }),
    ...(q.blocked === null ? {} : { blocked: q.blocked }),
  };
}

/** The address uses the same key names as the API, so one table maps both. */
export function toSearchParams(q: SearchQuery): URLSearchParams {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(toFilter(q))) {
    for (const v of Array.isArray(value) ? value : [value]) out.append(key, String(v));
  }
  // An explicit empty selection must survive refreshes; no parameter means all categories.
  if (q.categories.length === 0) out.set("category", "");
  if (q.sort !== DEFAULT_SORT) out.set("sort", q.sort);
  return out;
}

const unique = <T>(list: readonly T[]) => [...new Set(list)];
const known = <T extends string>(all: readonly T[], given: readonly string[]) =>
  all.filter((v) => given.includes(v));

/** Reads an address. Anything the contract would reject is dropped, never passed on. */
export function fromSearchParams(params: URLSearchParams): SearchQuery {
  const xtags = unique(params.getAll("xtag").map(normalizeTag).filter(Boolean));
  const tags = unique(params.getAll("tag").map(normalizeTag).filter(Boolean)).filter(
    (t) => !xtags.includes(t),
  );
  const rating = Number(params.get("ratingMin") ?? Number.NaN);
  const blocked = params.get("blocked");
  const sort = params.get("sort");
  return {
    gid: parseGid(params.get("gid") ?? ""),
    text: (params.get("q") ?? "").trim().slice(0, 200),
    tags,
    xtags,
    categories: params.has("category") ? known(CATEGORIES, params.getAll("category")) : CATEGORIES,
    states: known(GALLERY_STATES, params.getAll("state")),
    ratingMin: rating >= 0 && rating <= 5 ? Math.round(rating * 100) / 100 : null,
    blocked: blocked === "show" || blocked === "hide" ? blocked : null,
    sort: GALLERY_SORTS.find((s) => s === sort) ?? DEFAULT_SORT,
  };
}
